import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { initGA, trackPageView } from "@/lib/analytics";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import KYC from "@/pages/KYC";
import Assessment from "@/pages/Assessment";
import Results from "@/pages/Results";
import Matches from "@/pages/Matches";
import Shortlist from "@/pages/Shortlist";
import Report from "@/pages/Report";
import SharedReport from "@/pages/SharedReport";
import Chat from "@/pages/Chat";
import Subscription from "@/pages/Subscription";
import Notifications from "@/pages/Notifications";
import Compare from "@/pages/Compare";
import Readiness from "@/pages/Readiness";
import AdminTestimonials from "@/pages/AdminTestimonials";
import AdminKYC from "@/pages/AdminKYC";
import AdminPanel from "@/pages/AdminPanel";
import HowItWorks from "@/pages/HowItWorks";
import Kundali from "@/pages/Kundali";
import Family from "@/pages/Family";
import Philosophy from "@/pages/Philosophy";
import Moments from "@/pages/Moments";
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import Refund from "@/pages/legal/Refund";
import Shipping from "@/pages/legal/Shipping";
import Contact from "@/pages/legal/Contact";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Boots GA4 once and fires a page_view on every route change (covers all paths).
function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => { initGA(); }, []);
  useEffect(() => { trackPageView(location.pathname + location.search); }, [location]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AnalyticsTracker />
        <div className="App noise">
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/kundali" element={<Kundali />} />
            <Route path="/family" element={<Family />} />
            <Route path="/philosophy" element={<Philosophy />} />
            <Route path="/moments" element={<Moments />} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/report/share/:shareId" element={<SharedReport />} />

            {/* Legal / Compliance — public, required for Razorpay */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/refund" element={<Refund />} />
            <Route path="/shipping" element={<Shipping />} />
            <Route path="/contact" element={<Contact />} />

            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/kyc" element={<Protected><KYC /></Protected>} />
            <Route path="/assessment" element={<Protected><Assessment /></Protected>} />
            <Route path="/results" element={<Protected><Results /></Protected>} />
            <Route path="/matches" element={<Protected><Matches /></Protected>} />
            <Route path="/shortlist" element={<Protected><Shortlist /></Protected>} />
            <Route path="/readiness" element={<Protected><Readiness /></Protected>} />
            <Route path="/matches/:id/report" element={<Protected><Report /></Protected>} />
            <Route path="/chat/:id" element={<Protected><Chat /></Protected>} />
            <Route path="/subscription" element={<Protected><Subscription /></Protected>} />
            <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
            <Route path="/admin/testimonials" element={<Protected><AdminTestimonials /></Protected>} />
            <Route path="/admin/kyc" element={<Protected><AdminKYC /></Protected>} />
            <Route path="/admin/panel" element={<Protected><AdminPanel /></Protected>} />
          </Routes>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
